import React, { createContext, useContext } from 'react';
import { useLocalStorage } from 'react-use';
import {
  createGlobalStyle,
  css,
  ThemeProvider as OriginalThemeProviderBase,
} from 'styled-components';

const colors = {
  transparent: 'transparent',
  white: '#FFFFFF',
  black: '#1B1A1F',
  'blue-1': '#239AE7',
  'blue-2': '#158AD6',
  'blue-light': '#F1F9FF',
  green: '#C4FBD1',
  'green-light': '#F0FFF2',
  'danger-1': '#DD6C6C',
  'danger-2': '#CD5555',
  'danger-3': '#ED6E6E',
  'danger-4': '#FFE8EA',
  'danger-5': '#F74857',
  'danger-6': '#E78585',
  'danger-7': '#6A0E00',
  placeholder: '#B2B1BA',
  'light-grey': '#373737',
  'light-grey-2': '#F6F6F6',
  'light-grey-3': '#F9F7F7',
  'light-grey-4': '#E5E5E5',
  'light-mid-grey': '#F2F0F0',
  'warm-grey': '#ECEAF5',
  'mid-grey': '#676570',
  offline: '#E0E0E0',
  orange: '#FFC48E',
  'orange-light': '#FFF9F4',
  'orange-dark': '#FFA653',
} as const;

export type Color = (typeof colors)[keyof typeof colors];

export const theme = {
  mode: 'light',
  color(lightMode: Color, darkMode?: Color) {
    return this.mode === 'dark' ? (darkMode ?? lightMode) : lightMode;
  },
  zindex: {
    modalBackdrop: 90,
    nav: 100,
    modal: 110,
  },
};

export type Theme = typeof theme;

interface DefaultTheme extends Theme {}

const OriginalThemeProvider =
  OriginalThemeProviderBase as unknown as React.ComponentType<{
    theme: DefaultTheme;
    children: React.ReactNode;
  }>;

export const GlobalStyles = createGlobalStyle`${css`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html,
  body {
    height: 100%;
  }

  html,
  body,
  input,
  textarea,
  button,
  select {
    font-family: 'IBM Plex Sans', sans-serif;
    line-height: 1.5;
  }

  html {
    background: ${({ theme: globalTheme }: { theme: Theme }) =>
      globalTheme.color('#FFFFFF', '#1B1A1F')};
    color: ${({ theme: globalTheme }: { theme: Theme }) =>
      globalTheme.color('#1B1A1F')};
  }

  #root {
    min-height: 100%;
    height: 100%;
  }

  a,
  a:visited,
  a:hover,
  a:active {
    color: inherit;
  }
`}`;

const Context = createContext<(newValue: 'dark' | 'light') => void>(() =>
  console.error('App is not wrapped in a <ThemeProvider>'),
);

export const ThemeProvider = ({
  children,
}: {
  readonly children: React.ReactNode;
}) => {
  const [themeMode, setThemeMode] = useLocalStorage<'light' | 'dark'>(
    'theme',
    'light',
  );

  return (
    <Context.Provider value={setThemeMode}>
      <OriginalThemeProvider
        theme={{ ...theme, mode: themeMode === 'dark' ? 'dark' : 'light' }}
      >
        {children}
      </OriginalThemeProvider>
    </Context.Provider>
  );
};

export const useChangeTheme = () => useContext(Context);
